import React, { useState } from "react";

import { DragEndEvent, useDndMonitor } from "@dnd-kit/core";
import { InfoOutlined, PictureAsPdf } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { ImageEditor } from "@/components/alerts/add/ImageEditor";
import {
  ENTITY_TYPE_ICONS,
  entityChipLabel,
} from "@/components/alerts/add-v2/entities/entityTypeMeta";
import { AttachOption } from "@/components/alerts/add-v2/media/AttachMenu";
import { editedFilename } from "@/components/alerts/add-v2/media/editedFilename";
import { isViewable } from "@/components/alerts/add-v2/media/isViewable";
import { MediaDropZone } from "@/components/alerts/add-v2/media/MediaDropZone";
import { MediaLightbox } from "@/components/alerts/add-v2/media/MediaLightbox";
import { MediaList } from "@/components/alerts/add-v2/media/MediaList";
import { PdfRow } from "@/components/alerts/add-v2/media/PdfRow";
import { useMediaUploads } from "@/components/alerts/add-v2/media/useMediaUploads";
import { dropIntent } from "@/components/alerts/add-v2/model/dropIntent";
import { numberWithinType } from "@/components/alerts/add-v2/model/entityRules";
import { BulletinMediaItem } from "@/components/alerts/add-v2/model/types";
import { useBulletinRow } from "@/components/alerts/add-v2/persistence/bulletinRowContext";
import { useBulletinField } from "@/components/alerts/add-v2/state/bulletinFormContext";

// No bulletin in production has ever had a PDF attached, so its drop zone is only a second thing
// to scroll past. Everything behind it — the `pdf` field, the endpoints, `PdfRow` — is left in
// place, so flipping this back on is the whole restore.
const SHOW_BULLETIN_PDF = false;

/**
 * Images and video.
 *
 * Media is optional here — the "Add pictures?" interstitial is gone. Each file shows which entities
 * it is attached to; the entity cards show the same relation from the other side, and both write
 * through `model/mediaLinks` because the relation is stored once, on the media item.
 *
 * Uploads go straight to the bulletin's own endpoints rather than through the document autosave;
 * `useMediaUploads` and `persistence/mediaRepository` hold the reasons. The one thing to know before
 * touching this section: **the first upload is what creates the bulletin row** (see
 * `BulletinRowProvider`), so nothing here can be made to run on mount.
 *
 * The bulletin-PDF zone is hidden behind `SHOW_BULLETIN_PDF` below — decision 41.
 */
export const MediaSection: React.FC = () => {
  const { t } = useTranslation("bulletin/create-v2");
  const [media] = useBulletinField("media");
  const [pdf] = useBulletinField("pdf");
  // Read for the attach picker only. Entity edits therefore re-render this section, which is the
  // price of the labels being live — a chip reading "Person #1 · Wanted" has to follow the entity.
  const [entities] = useBulletinField("entities");
  const uploads = useMediaUploads();
  /**
   * Which file the lightbox is showing, by id rather than by index: the lightbox is handed only the
   * files it can show, so an index into `media` would point at the wrong one as soon as a bulletin
   * holds something that is neither an image nor a video.
   */
  const [expandedId, setExpandedId] = useState<string | null>(null);
  /** The image the crop/rotate editor is open on, by value: it needs the URL and the filename. */
  const [editing, setEditing] = useState<BulletinMediaItem | null>(null);
  const row = useBulletinRow();

  /**
   * Both halves of decision 44's gesture, because both start on a media card: dropped on another
   * card the file moves there, dropped on an entity card it attaches. The context belongs to the
   * page — a drop that crossed into another section could not be matched to a droppable otherwise —
   * so this subscribes to it rather than owning it, and ignores the drags that start elsewhere.
   */
  useDndMonitor({
    onDragEnd: ({ active, over }: DragEndEvent) => {
      const intent = dropIntent(active, over);
      if (!intent) return;

      if (intent.kind === "reorder-media") {
        uploads.reorderMedia(intent.activeId, intent.overId);
      }
      if (intent.kind === "attach") {
        // Dropping an already-attached file must not detach it, so this is not a toggle.
        const already = media
          .find((item) => item.id === intent.mediaId)
          ?.entityIds.includes(intent.entityId);
        if (!already) uploads.toggleEntity(intent.mediaId, intent.entityId);
      }
    },
  });

  /** What the lightbox can page through, which is not necessarily every file on the bulletin. */
  const viewable = media.filter(isViewable);

  const entityOptions = (item: BulletinMediaItem): AttachOption[] =>
    entities.map((entity) => ({
      id: entity.id,
      label: entityChipLabel(entity, numberWithinType(entities, entity), t),
      icon: ENTITY_TYPE_ICONS[entity.type],
      checked: item.entityIds.includes(entity.id),
    }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <MediaDropZone
        variant="tri"
        title={t("media.upload_media")}
        accept="image/*,video/*"
        multiple
        busy={uploads.busy}
        disabled={uploads.busy}
        onFiles={uploads.addMedia}
      />

      {media.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.75,
            color: "text.secondary",
          }}
        >
          <InfoOutlined sx={{ fontSize: 16, mt: "2px" }} />
          <Typography variant="caption">{t("media.attach_hint")}</Typography>
        </Box>
      )}

      {SHOW_BULLETIN_PDF &&
        (pdf === null ? (
          <MediaDropZone
            variant="row"
            icon={<PictureAsPdf />}
            title={t("media.upload_pdf")}
            hint={t("media.upload_pdf_hint")}
            accept="application/pdf,.pdf"
            busy={uploads.busy}
            disabled={uploads.busy}
            onFiles={(files) => uploads.setPdf(files[0])}
          />
        ) : (
          <PdfRow
            pdf={pdf}
            disabled={uploads.busy}
            onDelete={uploads.removePdf}
          />
        ))}

      {media.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {t("media.empty")}
        </Typography>
      ) : (
        <MediaList
          items={media}
          entityOptions={entityOptions}
          disabled={uploads.busy}
          onDelete={uploads.removeMedia}
          onToggleEntity={(item, entityId) =>
            uploads.toggleEntity(item.id, entityId)
          }
          onExpand={(item) => setExpandedId(item.id)}
          onEdit={setEditing}
        />
      )}

      {editing && (
        /*
         * The production editor, unmodified — crop presets, zoom to 10x, the rotation slider and
         * the 90-degree step are all its own (`../add/ImageEditor`, react-easy-crop).
         *
         * It is wrapped because its own container is `position: fixed; z-index: 2`, which was
         * enough on a wizard step and is not enough here: the app bar is 1100 and this page has a
         * fixed action bar at the same level, so unwrapped the editor opens *underneath* both. The
         * wrapper is a stacking context at the modal level, which lifts the whole thing with it
         * rather than editing a stylesheet the old flow still uses.
         */
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal,
          }}
        >
          <ImageEditor
            imageObject={{
              download_url: editing.url,
              filename: editing.filename,
            }}
            bulletinID={row.id() ?? 0}
            onCancel={() => setEditing(null)}
            handleImageUpload={(_filename, _bulletinId, file) => {
              // The editor hands back a JPEG under the original name; the replacement needs one of
              // its own, because the upload happens before the delete. See `editedFilename`.
              const renamed = new File(
                [file],
                editedFilename(
                  editing.filename,
                  media.map((item) => item.filename),
                ),
                { type: file.type },
              );
              setEditing(null);
              void uploads.replaceMedia(editing, renamed);
            }}
          />
        </Box>
      )}

      <MediaLightbox
        items={viewable}
        index={viewable.findIndex((item) => item.id === expandedId)}
        onClose={() => setExpandedId(null)}
      />
    </Box>
  );
};
